import { TextLink } from '@rsa-digital/evo-shared-components/commonTypes/links';
import { SecondaryCta } from '@rsa-digital/evo-shared-components/components/Cta';
import { GridItem } from '@rsa-digital/evo-shared-components/components/Grid';
import { graphql } from 'gatsby';
import React from 'react';
import {
  processImageAsset,
  processOptionalCta,
  unwrapSingleton,
} from 'helpers/csTypeProcessors';
import { handleAndTrackLinkClick } from 'helpers/navigation';
import { CsCta } from 'types/contentStack';
import {
  CtaWrapper,
  DetailedRelatedContentContainer,
  StyledCtaGrid,
  StyledDetailedRelatedContentWithVisibilityToggle,
} from './styles';
import { RelatedContentCardData } from '../types';

type DetailedRelatedContentBlockProps = {
  heading: string | null;
  intro: string | null;
  html_id?: string;
  display_background: boolean;
  content_cards: RelatedContentCardData[];
  cta: [CsCta] | null;
};

export const query = graphql`
  fragment DetailedRelatedContentBlock on cs__detailed_related_content {
    html_id
    heading
    intro
    display_background
    content_cards {
      ...RelatedContentCardBlock
    }
    cta {
      ...Cta
    }
  }
`;

const generateReadMorePropForCard = (
  card: RelatedContentCardData
): TextLink | undefined => {
  if (card.read_more?.text && card.read_more?.url) {
    return {
      url: card.read_more.url,
      onClick: handleAndTrackLinkClick(
        card.url,
        'detailedRelatedContentLink',
        card.read_more.text
      ),
      text: card.read_more.text,
      openInNewTab: card.read_more.open_in_new_tab,
    };
  }

  if (!card.read_more?.text && !card.read_more?.url) {
    return undefined;
  }

  throw Error('Url and text must both be present or neither present for Link!');
};
const DetailedRelatedContentBlock: React.FC<{
  detailed_related_content: DetailedRelatedContentBlockProps;
}> = ({ detailed_related_content }) => {
  const cta = processOptionalCta(
    detailed_related_content.cta,
    'detailedRelatedContentCta'
  );

  return (
    <DetailedRelatedContentContainer
      data-cy="DetailedRelatedContent"
      id={detailed_related_content.html_id}
      hasCta={!!cta}
      displayBackground={detailed_related_content.display_background}>
      <StyledDetailedRelatedContentWithVisibilityToggle
        heading={detailed_related_content.heading ?? undefined}
        intro={detailed_related_content.intro ?? undefined}
        cardProps={detailed_related_content.content_cards.map((card) => ({
          url: card.url,
          onClick: handleAndTrackLinkClick(
            card.url,
            'detailedRelatedContentWithVisibilityToggleLink',
            card.heading
          ),
          image: processImageAsset(card.image),
          heading: card.heading,
          subheading: card.subheading ?? undefined,
          body: card.body ?? undefined,
          readTime: card.read_time ?? undefined,
          readTimeText: card.read_time_text ?? undefined,
          readTimeIcon: unwrapSingleton(card.read_time_icon)?.icon_code,
          readMore: generateReadMorePropForCard(card),
          max_lines: card.max_lines ?? undefined,
        }))}
      />
      {cta && (
        <StyledCtaGrid>
          <GridItem>
            <CtaWrapper>
              <SecondaryCta cta={cta} />
            </CtaWrapper>
          </GridItem>
        </StyledCtaGrid>
      )}
    </DetailedRelatedContentContainer>
  );
};

export default React.memo(DetailedRelatedContentBlock);
